'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    deleteNote,
    FISCAL_NOTE_STATUS_COLORS,
    FISCAL_NOTE_STATUS_LABELS,
    FISCAL_NOTE_TYPE_LABELS,
    FiscalNote,
    FiscalNoteStatus,
    FiscalNoteType,
    getNotes,
} from '@/lib/api/fiscal/fiscal-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { SocketContext } from '@/lib/providers/socket-provider'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useContext, useEffect, useState } from 'react'
import { MdAttachMoney, MdRefresh, MdSearch } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const FiscalNotesPage = () => {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canDelete } = useResourcePermissions('fiscal', 'invoices')
    const { socket } = useContext(SocketContext)

    const [notes, setNotes] = useState<FiscalNote[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [showSearch, setShowSearch] = useState(false)
    const [typeFilter, setTypeFilter] = useState<FiscalNoteType | ''>('')
    const [statusFilter, setStatusFilter] = useState<FiscalNoteStatus | ''>('')
    const [deleteId, setDeleteId] = useState<number | null>(null)
    const [deleteLoading, setDeleteLoading] = useState(false)

    useEffect(() => {
        if (user?.companyId) {
            loadNotes()
        }
    }, [user?.companyId])

    useEffect(() => {
        if (socket && user?.companyId) {
            const event = `fiscal-note-updated-${user.companyId}`
            socket.on(event, () => loadNotes())
            return () => { socket.off(event) }
        }
    }, [socket, user?.companyId])

    const loadNotes = async () => {
        setLoading(true)
        const response = await getNotes()
        if (response.success) {
            setNotes(response.result || [])
        } else {
            toast.error(response.error)
        }
        setLoading(false)
    }

    const filteredNotes = notes.filter(note => {
        if (typeFilter && note.type !== typeFilter) return false
        if (statusFilter && note.status !== statusFilter) return false
        if (searchTerm) {
            const term = searchTerm.toLowerCase()
            return (
                note.numero?.toLowerCase().includes(term) ||
                note.destinatarioNome?.toLowerCase().includes(term) ||
                note.destinatarioCnpjCpf?.includes(term) ||
                note.ref.toLowerCase().includes(term)
            )
        }
        return true
    })

    const handleDelete = async () => {
        if (!deleteId) return
        setDeleteLoading(true)
        const response = await deleteNote(deleteId)
        if (response.success) {
            toast.success('Nota removida com sucesso')
            loadNotes()
        } else {
            toast.error(response.error)
        }
        setDeleteLoading(false)
        setDeleteId(null)
    }

    const formatCurrency = (cents: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(cents / 100)
    }

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold">Notas Fiscais</h1>
                <div className="flex items-center gap-2">
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => setShowSearch(!showSearch)}
                    >
                        <MdSearch size={20} />
                    </button>
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={loadNotes}
                    >
                        <MdRefresh size={20} />
                    </button>
                    {canCreate && (
                        <>
                            <button
                                className="btn btn-info btn-sm"
                                onClick={() => navigate('/fiscal/invoices/nfe')}
                            >
                                NF-e
                            </button>
                            <button
                                className="btn btn-accent btn-sm"
                                onClick={() => navigate('/fiscal/invoices/nfse')}
                            >
                                NFS-e
                            </button>
                        </>
                    )}
                </div>
            </div>

            {showSearch && (
                <div className="mb-4">
                    <input
                        type="text"
                        placeholder="Buscar por número, destinatário, CNPJ/CPF..."
                        className="input input-bordered w-full"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            )}

            <div className="flex gap-2 mb-4 flex-wrap">
                <select
                    className="select select-bordered select-sm"
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value as FiscalNoteType | '')}
                >
                    <option value="">Todos os tipos</option>
                    <option value="NFE">NF-e</option>
                    <option value="NFSE">NFS-e</option>
                </select>
                <select
                    className="select select-bordered select-sm"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as FiscalNoteStatus | '')}
                >
                    <option value="">Todos os status</option>
                    <option value="PENDING">Pendente</option>
                    <option value="PROCESSING">Processando</option>
                    <option value="AUTHORIZED">Autorizada</option>
                    <option value="DENIED">Rejeitada</option>
                    <option value="CANCELLED">Cancelada</option>
                    <option value="ERROR">Erro</option>
                </select>
            </div>

            {loading ? (
                <div className="flex justify-center py-12">
                    <span className="loading loading-spinner loading-lg"></span>
                </div>
            ) : filteredNotes.length === 0 ? (
                <div className="text-center py-12 text-base-content/60">
                    Nenhuma nota fiscal encontrada
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredNotes.map((note) => (
                        <div
                            key={note.id}
                            className="card bg-base-100 shadow-sm border border-base-200 hover:shadow-md transition-shadow cursor-pointer"
                            onClick={() => navigate(`/fiscal/invoices/${note.id}`)}
                        >
                            <div className="card-body p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <span className={`badge badge-sm ${note.type === 'NFE' ? 'badge-info' : 'badge-accent'}`}>
                                        {FISCAL_NOTE_TYPE_LABELS[note.type]}
                                    </span>
                                    <span className={`badge badge-sm ${FISCAL_NOTE_STATUS_COLORS[note.status]}`}>
                                        {FISCAL_NOTE_STATUS_LABELS[note.status]}
                                    </span>
                                </div>
                                <div className="font-semibold text-sm">
                                    {note.numero ? `N. ${note.numero}` : note.ref}
                                </div>
                                <div className="text-sm text-base-content/70 truncate">
                                    {note.destinatarioNome || 'Sem destinatário'}
                                </div>
                                {note.billing && (
                                    <div className="flex items-center gap-1 text-xs text-base-content/60">
                                        <MdAttachMoney className="text-sm" />
                                        <span>
                                            Cobrança #{note.billing.id} -
                                            <span className={`ml-1 font-medium ${
                                                note.billing.status === 'PAID' ? 'text-success' :
                                                note.billing.status === 'PENDING' ? 'text-warning' :
                                                note.billing.status === 'CANCELLED' ? 'text-error' :
                                                ''
                                            }`}>
                                                {note.billing.status === 'PAID' ? 'Pago' :
                                                 note.billing.status === 'PENDING' ? 'Pendente' :
                                                 note.billing.status === 'OVERDUE' ? 'Vencida' :
                                                 note.billing.status === 'CANCELLED' ? 'Cancelada' :
                                                 note.billing.status}
                                            </span>
                                        </span>
                                    </div>
                                )}
                                <div className="flex items-center justify-between mt-2">
                                    <span className="font-bold text-secondary">
                                        {formatCurrency(note.valorTotal)}
                                    </span>
                                    <span className="text-xs text-base-content/50">
                                        {format(new Date(note.createdAt), 'dd/MM/yyyy', { locale: ptBR })}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ConfirmModal
                isOpen={deleteId !== null}
                onClose={() => setDeleteId(null)}
                onConfirm={handleDelete}
                title="Remover nota fiscal"
                message="Tem certeza de que deseja remover esta nota fiscal?"
                loading={deleteLoading}
                confirmButtonText="Remover"
                confirmButtonClass="btn btn-error"
            />
        </div>
    )
}

export default FiscalNotesPage
